import { useState, useRef } from 'react';
import { Download, Replace, Trash2, Upload } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import { useConfirm } from '../ui/Confirm';
import { clean } from '../shell/copy';

// The editor's NLE project file on a Live production (latest only), on src/ui (HD-8: the Project file panel of the
// Workbench). presign, a direct PUT to S3, confirm; download through a short-lived signed URL. Project files only (no
// media): the server caps them (250 MB). Same calls as before: POST /api/live-production/:id/project-file/presign,
// PUT <uploadUrl>, POST …/project-file/confirm, GET …/project-file/download, DELETE …/project-file (now a two-step
// confirm). Not a footage upload, so it keeps its own determinate bar rather than the global Upload sheet.
const ACCEPT = '.prproj,.drp,.aep,.fcpxml,.fcpxmld,.xml,.aaf,.zip';

function fmtBytes(b) {
  if (b == null) return '';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3)   return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}
function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

export default function ProjectFileField({ record, canEdit, members, onChange }) {
  const confirm = useConfirm();
  const [phase,    setPhase]    = useState('idle'); // idle | uploading | confirming | downloading | error
  const [progress, setProgress] = useState(0);
  const [error,    setError]    = useState('');
  const inputRef = useRef(null);

  const hasFile     = !!record.projectFileName;
  const uploaderName = members?.find(m => m.id === record.projectFileUploadedBy)?.name || null;
  const busy = phase === 'uploading' || phase === 'confirming';

  async function handleFile(file) {
    if (!file) return;
    setError(''); setPhase('uploading'); setProgress(0);
    try {
      const presign = await api.post(`/api/live-production/${record.id}/project-file/presign`, {
        fileName: file.name, fileSize: file.size, contentType: file.type || 'application/octet-stream',
      });
      const { uploadUrl, s3Key } = presign.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`The upload stopped (${xhr.status}). Try again.`));
        xhr.onerror = () => reject(new Error('The network dropped during the upload. Try again.'));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });

      setPhase('confirming');
      const res = await api.post(`/api/live-production/${record.id}/project-file/confirm`, {
        s3Key, fileName: file.name, fileSize: file.size,
      });
      onChange(res.data); // merge new project-file fields into the parent record
      setPhase('idle'); setProgress(0);
    } catch (err) {
      setPhase('error');
      setError(err.response?.data?.error || err.message || 'The upload did not finish. Try again.');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDownload() {
    setError(''); setPhase('downloading');
    try {
      const res = await api.get(`/api/live-production/${record.id}/project-file/download`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      setError(err.response?.data?.error || 'The download did not start. Try again.');
    } finally { setPhase('idle'); }
  }

  async function handleRemove() {
    const ok = await confirm({ title: 'Remove the project file?', body: `${record.projectFileName} is deleted from storage. The next editor will have no project to open.`, confirmLabel: 'Remove file', destructive: true });
    if (!ok) return;
    setError(''); setPhase('confirming');
    try {
      const res = await api.delete(`/api/live-production/${record.id}/project-file`);
      onChange(res.data);
      setPhase('idle');
    } catch (err) {
      setPhase('error');
      setError(err.response?.data?.error || 'The file was not removed. Try again.');
    }
  }

  const meta = [fmtBytes(record.projectFileSize), clean(uploaderName), fmtDate(record.projectFileUploadedAt)].filter(Boolean).join(', ');
  return (
    <div className="ui-stack" data-testid="project-file">
      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={e => handleFile(e.target.files?.[0])} />

      {hasFile ? (
        <div className="ui-stack ui-stack--tight">
          <span className="ui-dense ui-anywhere">{record.projectFileName}</span>
          {meta && <span className="ui-meta">{meta}</span>}
          <span className="ui-cluster">
            <Button variant="secondary" icon={Download} onClick={handleDownload} loading={phase === 'downloading'}>Download</Button>
            {canEdit && <Button variant="quiet" icon={Replace} onClick={() => inputRef.current?.click()} disabled={busy}>Replace</Button>}
            {canEdit && <Button variant="danger" icon={Trash2} onClick={handleRemove} disabled={busy}>Remove</Button>}
          </span>
        </div>
      ) : canEdit ? (
        <div className="ui-stack ui-stack--tight">
          <p className="ui-p ui-dense ui-text-2">No project file yet. Upload the Premiere or Resolve project when the edit starts, so the next editor can open it.</p>
          <span><Button variant="secondary" icon={Upload} onClick={() => inputRef.current?.click()} disabled={busy}>Upload project file</Button></span>
        </div>
      ) : (
        <p className="ui-p ui-dense ui-text-3">No project file uploaded.</p>
      )}

      {busy && (
        <div className="ui-stack ui-stack--tight" role="status">
          <span className="ui-meter" role="progressbar" aria-label="Project file upload" aria-valuemin={0} aria-valuemax={100} aria-valuenow={phase === 'confirming' ? 100 : progress}>
            <span className="ui-meter__fill" style={{ width: `${phase === 'confirming' ? 100 : progress}%` }} />
          </span>
          <span className="ui-meta">{phase === 'confirming' ? 'Saving' : `Uploading, ${progress}%`}</span>
        </div>
      )}

      {error && <span className="ui-field__error" role="alert">{clean(error)}</span>}
    </div>
  );
}
